import { containers } from '@ecosystem/newron-design-system';
import { actionCreatorsSideFilters } from '@state/Index';
import { productsResponseConfig } from '@utils/FilterSettings';
import { getDisplayValue, isMulti, updateSelectedFilters } from '@utils/filterTrayUtils/FilterTrayUtils';
import { PagesConfig, usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { FilterKeys, FilterObject, Filters, Option, PageType, SubGroupFilter } from '@utils/pageConfig/Types';
import { getIsDisabledStatus } from '@utils/productGroupUtils/ProductGroupUtils';
import { cloneDeep } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import DatePicker from './DatePicker';
import * as styles from './FilterComponentProduct.module.scss';
import PriceInput from './PriceInput';
import HitlistUpsellContainer from './hitlist-upsell-banner/HitlistUpsellContainer';
import FilterSelectorProduct from '@components/list-filters-product/FilterSelectorProduct';
import { canShowExclusionToggle, isExclusionActiveForFilter } from '@utils/FilterUtils';
import { ExclusionProps } from '@components/design/WithMultiSelectToggle';

/* eslint-disable-next-line */
export interface SubFilterProps {
  filterObject: FilterObject;
  pageType: PageType;
  currencySymbol: string;
}

const FilterComponentProduct: React.FC<SubFilterProps> = ({ filterObject, pageType, currencySymbol }) => {
  const pagesConfig = usePageConfig();

  const dispatch = useDispatch();
  const { updateSideFilters } = bindActionCreators(actionCreatorsSideFilters, dispatch);

  const selectedValue = (selectFilter: Filters, options: Option[]) => {
    return getDisplayValue(pagesConfig, updateSideFilters, PageType.RI_PRODUCT, selectFilter, options);
  };

  const isMultiSelector = (selectFilter: Filters) => {
    return isMulti(selectFilter.filterKey, pageType, pagesConfig);
  };

  const onChangeFunc = (toUpdate: Filters) => {
    updateSelectedFilters(pagesConfig, pagesConfig.pageType, toUpdate);
  };

  const getFilterClassName = (filterKey: FilterKeys, filterTitle: string) => {
    const baseClassName = filterTitle.toLowerCase();
    const filterType = isMulti(filterKey, pageType, pagesConfig) ? 'multi' : 'single';
    return `${filterType}-${baseClassName}-filter`;
  };

  const filterItemTestId = (selectFilter) => {
    return selectFilter.filterKey === FilterKeys.ADDFEAT
      ? `filter-item-${selectFilter.filterKey}-${selectFilter.filterTitle}`
      : `filter-item-${selectFilter.filterKey}`;
  };

  const selectComponent = (filter: SubGroupFilter | FilterObject) => {
    if (!filter?.filters?.[0]) {
      return;
    }

    if (isMaxNumOfModelNotNull() && pagesConfig.isSideFiltersFeatureFlagEnabled) {
      return <HitlistUpsellContainer />;
    } else {
      return (
        <div data-test-id="filter-items">
          {filter.filters.map((selectFilter) => {
            const options: Option[] = selectFilter.options;

            const exclusion: ExclusionProps = {
              isAvailable: canShowExclusionToggle(selectFilter.filterKey, pageType),
              isActive: isExclusionActiveForFilter(selectFilter.filterKey, pageType, pagesConfig),
            };

            return (
              // eslint-disable-next-line jsx-a11y/no-static-element-interactions
              <div
                key={`${options}-${selectFilter.filterTitle}`} //NOSONAR typescript:S6551
                data-test-id={filterItemTestId(selectFilter)}
                className={`${getFilterClassName(selectFilter.filterKey, selectFilter.filterTitle)} mb-5`}
              >
                <FilterSelectorProduct
                  isMultiSelector={isMultiSelector(selectFilter)}
                  selectFilter={selectFilter}
                  selectedValue={selectedValue(selectFilter, selectFilter.options)}
                  filterObject={filterObject}
                  exclusion={exclusion}
                  onChangeFunc={onChangeFunc}
                />
              </div>
            );
          })}
        </div>
      );
    }
  };

  const isMaxNumOfModelNotNull = () => {
    //if maxNumOfModels for pg not null the feature-value api return 'disabled' ===true for 'dataType' BRAND.
    //if 'disabled' true, disable all selection except Base and Market Definition
    return (
      filterObject.mainTitle !== 'Base' &&
      filterObject.mainTitle !== 'Market Definition' &&
      getIsDisabledStatus(productsResponseConfig.response[2].filters)
    );
  };

  const toRenderWithAccordionProduct = () => {
    const { Accordion } = containers;
    return (
      <>
        {filterObject.subGroupFilters.map((subGroup) => (
          <Accordion flushAlign={true} key={subGroup.subGroupTitle} hoverColor="bg-medium" iconRight={true}>
            <Accordion.Item
              title={subGroup.subGroupTitle}
              data-test-id="filter-accordion"
              disabled={isMaxNumOfModelNotNull()}
            >
              {selectComponent(subGroup)}
            </Accordion.Item>
          </Accordion>
        ))}
      </>
    );
  };

  const toRenderSubGroupProduct = () => {
    const allEmpty = filterObject.subGroupFilters.every(function (filter) {
      return filter.filters.length === 0;
    });

    const mainFilterOptionsEmpty = filterObject.filters.every(function (filter) {
      return filter.options.length === 0;
    });

    const subGroupTitles = filterObject.subGroupFilters.map((e) => e.subGroupTitle);

    if (filterObject.hasNormalAndSubGroup && !allEmpty) {
      return (
        <div>
          <div>{selectComponent(filterObject)}</div>
          <div>{toRenderWithAccordionProduct()}</div>
        </div>
      );
    }

    /*
     * if it doesn't have normal filter but has subgroup filters, and there are no valid filters under the subgroup
     * eg. PG that has no Product Features at all
     */
    if (filterObject.hasSubGroup && !filterObject.hasNormalAndSubGroup && allEmpty) {
      return (
        <div>
          <div>{selectComponent(filterObject)}</div>
          {subGroupTitles.includes('First Activity') && !mainFilterOptionsEmpty
            ? toRenderDatePicker(updateSelectedFilters)
            : null}
          {subGroupTitles.includes('Average Price') && !mainFilterOptionsEmpty
            ? toRenderInputWithPrepend(updateSelectedFilters)
            : null}
        </div>
      );
    }

    // if it does not have subgroup filters
    return (
      <div>
        <div>{toRenderWithAccordionProduct()}</div>
      </div>
    );
  };

  /*
   * function that gets the filters to be displayed in the Date Picker/Price Input component of the filters
   */
  const getPairValues = (currentFilterA: Filters, currentFilterB: Filters, toReturnA: any, toReturnB: any) => {
    const filters = [...pagesConfig.selectedFilters[pageType].selectedFilters];
    const deepCopyFilters = cloneDeep(filters);

    const indexA = deepCopyFilters.findIndex((e) => e.filterKey === currentFilterA.filterKey);
    const indexB = deepCopyFilters.findIndex((e) => e.filterKey === currentFilterB.filterKey);

    if (indexA !== -1 && currentFilterA.filterTitle === deepCopyFilters[indexA].filterTitle) {
      toReturnA = deepCopyFilters[indexA].options[0].label;
    }
    if (indexB !== -1 && currentFilterB.filterTitle === deepCopyFilters[indexB].filterTitle) {
      toReturnB = deepCopyFilters[indexB].options[0].label;
    }

    return { toReturnA: toReturnA, toReturnB: toReturnB };
  };

  /*
   * function that gets the filters to be displayed in the Date Picker component of the filters
   */
  const getDisplayValueDatePicker = (currentFilterStartDate: Filters, currentFilterEndDate: Filters) => {
    let startDate = undefined;
    let endDate = undefined;

    const toReturn = getPairValues(currentFilterStartDate, currentFilterEndDate, startDate, endDate);
    startDate = toReturn.toReturnA;
    endDate = toReturn.toReturnB;

    if (toReturn.toReturnA !== undefined) {
      // @ts-ignore
      startDate = new Date(startDate);
    }

    if (endDate !== undefined) {
      // @ts-ignore
      endDate = new Date(endDate);
    }
    return { startDate: startDate, endDate: endDate };
  };

  /*
   * returns the Date Picker component
   */
  const toRenderDatePicker = (
    updateFilterCallback: (pagesConfig: PagesConfig, pageType: PageType, toUpdate: Filters) => void,
  ) => {
    return (
      <div>
        <div className={styles.FilterComponentProduct__labelTitle}>{'First Activity'}</div>
        <DatePicker
          updateFilterCallback={updateFilterCallback}
          getDisplayValueDatePicker={getDisplayValueDatePicker}
          disabled={isMaxNumOfModelNotNull()}
        />
        <br />
      </div>
    );
  };

  /*
   * function that gets the filters to be displayed in the Price Input component of the filters
   */
  const getDisplayValueMinMax = (currentFilterMinValue: Filters, currentFilterMaxValue: Filters) => {
    let selectedMinValue = '';
    let selectedMaxValue = '';
    const toReturn = getPairValues(currentFilterMinValue, currentFilterMaxValue, selectedMinValue, selectedMaxValue);

    return { selectedMinValue: toReturn.toReturnA, selectedMaxValue: toReturn.toReturnB };
  };

  /*
   * returns the Price input component
   */
  const toRenderInputWithPrepend = (
    updateFilterCallback: (pagesConfig: PagesConfig, pageType: PageType, toUpdate: Filters) => void,
  ) => {
    return (
      <div>
        <div className={styles.FilterComponentProduct__labelTitle}>{'Average Price'}</div>
        <PriceInput
          updateFilterCallback={updateFilterCallback}
          getDisplayValueMinMax={getDisplayValueMinMax}
          currencySymbol={currencySymbol}
          disabled={isMaxNumOfModelNotNull()}
        />
      </div>
    );
  };

  /*
   * checks which component to return
   */
  const toRenderComponentType = (fObject: FilterObject) => {
    if (fObject.hasSubGroup) {
      return toRenderSubGroupProduct();
    } else {
      return selectComponent(fObject);
    }
  };

  return (
    <div className={styles.FilterComponentProduct}>
      {!pagesConfig.isSideFiltersFeatureFlagEnabled && (
        <div className={styles.FilterComponentProduct__mainTitle}>{filterObject.mainTitle}</div>
      )}
      <div>{toRenderComponentType(filterObject)}</div>
    </div>
  );
};

export default FilterComponentProduct;
